import { cloneElement, useCallback, useEffect, useRef } from 'react'
import { useMediaQuery, useMountEffect } from '@react-hookz/web'
import { clsx } from 'clsx'
import { debounce } from 'lodash-es'

import { LoadingState } from 'Components/LoadingState/LoadingState'

import { setContainerWidth } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'

import { PreviewColumnProps } from './PreviewColumn.types'
import { PreviewColumnFooter } from './PreviewColumnFooter'

export const PreviewColumn = (props: PreviewColumnProps) => {
  const {
    id,
    type,
    url,
    contentElement,
    columnRef,
    headerElement,
    isOpen,
    isLoading,
    onClosePreviewColumn,
    disableFooterAskQuestion,
    renderCustomFooterButton,
  } = props

  const containerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)

  const isDesktop = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.XXL})`)

  const setPreviewColumnWidth = useCallback(() => {
    setContainerWidth(containerRef, columnRef, isDesktop)
  }, [columnRef, isDesktop])

  useMountEffect(() => {
    setPreviewColumnWidth()
  })

  const updateContentHeight = useCallback(() => {
    const { current: container } = containerRef
    const { current: header } = headerRef
    const { current: content } = contentRef
    const { current: footer } = footerRef

    if (!container || !header || !content || !footer) {
      return
    }

    const { height: containerHeight } = container.getBoundingClientRect()
    const { height: headerHeight } = header.getBoundingClientRect()
    const { height: footerHeight } = footer.getBoundingClientRect()

    content.style.height = `${containerHeight - headerHeight - footerHeight}px`
  }, [])

  useEffect(() => {
    updateContentHeight()
  }, [isLoading, updateContentHeight])

  useEffect(() => {
    const debouncedUpdateContainerWidth = debounce(setPreviewColumnWidth, 250)
    const debouncedUpdateContentHeight = debounce(updateContentHeight, 10)

    window.addEventListener('resize', debouncedUpdateContainerWidth)
    window.addEventListener('resize', debouncedUpdateContentHeight)

    return () => {
      window.removeEventListener('resize', debouncedUpdateContainerWidth)
      window.removeEventListener('resize', debouncedUpdateContentHeight)
    }
  }, [setPreviewColumnWidth, updateContentHeight])

  return (
    <div
      ref={containerRef}
      className={clsx(
        'PreviewColumn',
        id && isOpen && 'PreviewColumn--is-open',
        disableFooterAskQuestion && 'PreviewColumn--no-qBox',
      )}
    >
      {isLoading ? (
        <div className="PreviewColumn-loader">
          <LoadingState />
        </div>
      ) : (
        <>
          {headerElement && cloneElement(headerElement, { ref: headerRef })}

          {contentElement && cloneElement(contentElement, { ref: contentRef })}
        </>
      )}

      <PreviewColumnFooter
        ref={footerRef}
        disableAskQuestion={disableFooterAskQuestion}
        id={id}
        isOpen={isOpen}
        onClosePreviewColumn={onClosePreviewColumn}
        renderCustomFooterButton={renderCustomFooterButton}
        type={type}
        url={url}
      />
    </div>
  )
}
